/**
 * Utility to check if a feature exists in merchant configuration
 */
const hasFeature = (configuration: any, feature: string) => {
  if (!configuration) {
    return false;
  }
  if (Array.isArray(configuration)) {
    return configuration.includes(feature);
  }
  return configuration === feature;
};
import { DesktopOutlined, MobileOutlined } from '@ant-design/icons';
import React, { useMemo } from 'react';

import { Divider, FormGroup, Space } from 'components';
import { FormGroupItem } from 'components/FormGroupItem';

import { styled } from 'theme';
import type { Firebase } from 'types/firebase';
import { getOptionValueText } from 'utils/OptionUtils';
import {
  NOTES_FEATURE,
  QR_PAYMENT_FEATURE,
  UNLINKED_REFUND_FEATURE,
  DISCOUNT_AMOUNT_FEATURE,
  QR_PAYMENT_INITIATION_ENABLED_FEATURE,
  AI_FORECAST_ENABLED_FEATURE,
  RECEIPT_PRINTING_FEATURE,
  MULTIMERCHANT_ENABLED_FEATURE,
  SEND_PAYMENT_LINK_FROM_PORTAL_ENABLED_FEATURE,
  MFA_ENABLED_FEATURE,
  REFUND_PAYMENT_LINK_ENABLED_FEATURE,
  // PAYBOX_ENABLED_FEATURE,
} from '../../../../constants';
/**
 * Styling
 */
const Container = styled.div``;

/**
 * Types
 */
export type MerchantConfigurationProps = {
  merchant: Firebase.Merchant;
  onSave: (name: string, value: string | number) => void;
};

type FormFieldType = {
  title: string;
  icon: React.ReactNode;
  inputs: {
    type?: string;
    label: string;
    alternativeLabel: string;
    name: string;
    value: string | number | null;
  }[];
};

/**
 * MerchantConfiguration component
 */
const MerchantConfiguration: React.FC<MerchantConfigurationProps> = (props) => {
  const { merchant } = props;
  // aiForecastEnabled
  const hasAiForecastEnabled = hasFeature(
    merchant?.configuration,
    'aiForecastEnabled',
  );
  const hasAiForecastEnabledValue = hasAiForecastEnabled
    ? 'enable_aiForecastEnabled'
    : 'disable_aiForecastEnabled';
  const aiForecastEnabledValueText = getOptionValueText(
    AI_FORECAST_ENABLED_FEATURE,
    hasAiForecastEnabledValue,
  );
  // Feature checks using util
  const hasDiscountAmount = hasFeature(
    merchant?.configuration,
    'discountAmount',
  );
  const hasDiscountAmountValue = hasDiscountAmount
    ? 'enable_discountAmount'
    : 'disable_discountAmount';
  const discountAmountValueText = getOptionValueText(
    DISCOUNT_AMOUNT_FEATURE,
    hasDiscountAmountValue,
  );

  const hasQrPaymentInitiationEnabled = hasFeature(
    merchant?.configuration,
    'qrPaymentInitiationEnabled',
  );
  const hasQrPaymentInitiationEnabledValue = hasQrPaymentInitiationEnabled
    ? 'enable_qrPaymentInitiationEnabled'
    : 'disable_qrPaymentInitiationEnabled';
  const qrPaymentInitiationEnabledValueText = getOptionValueText(
    QR_PAYMENT_INITIATION_ENABLED_FEATURE,
    hasQrPaymentInitiationEnabledValue,
  );

  const hasNotes = hasFeature(merchant?.configuration, 'notes');
  const hasNotesValue = hasNotes ? 'enable_notes' : 'disable_notes';
  const notesValueText = getOptionValueText(NOTES_FEATURE, hasNotesValue);

  const hasQrPayments = hasFeature(merchant?.configuration, 'qrPayment');
  const hasQrPaymentsValue = hasQrPayments
    ? 'enable_qrPayment'
    : 'disable_qrPayment';
  const qrPaymentsValueText = getOptionValueText(
    QR_PAYMENT_FEATURE,
    hasQrPaymentsValue,
  );

  const hasUnlinkedRefund = hasFeature(
    merchant?.configuration,
    'unlinkedRefund',
  );
  const hasUnlinkedRefundValue = hasUnlinkedRefund
    ? 'enable_unlinkedRefund'
    : 'disable_unlinkedRefund';
  const unlinkedRefundValueText = getOptionValueText(
    UNLINKED_REFUND_FEATURE,
    hasUnlinkedRefundValue,
  );

  const hasReceiptPrinting = hasFeature(
    merchant?.configuration,
    'receiptPrinting',
  );
  const hasReceiptPrintingValue = hasReceiptPrinting
    ? 'enable_receiptPrinting'
    : 'disable_receiptPrinting';
  const receiptPrintingValueText = getOptionValueText(
    RECEIPT_PRINTING_FEATURE,
    hasReceiptPrintingValue,
  );

  const hasSendPaymentLinkFromPortalEnabled = hasFeature(
    merchant?.configuration,
    'sendPaymentLinkFromPortalEnabled',
  );
  const hasSendPaymentLinkFromPortalEnabledValue = hasSendPaymentLinkFromPortalEnabled
    ? 'enable_sendPaymentLinkFromPortalEnabled'
    : 'disable_sendPaymentLinkFromPortalEnabled';
  const sendPaymentLinkFromPortalEnabledValueText = getOptionValueText(
    SEND_PAYMENT_LINK_FROM_PORTAL_ENABLED_FEATURE,
    hasSendPaymentLinkFromPortalEnabledValue,
  );

  const hasMfaEnabled = hasFeature(merchant?.configuration, 'mfaEnabled');
  const hasMfaEnabledValue = hasMfaEnabled
    ? 'enable_mfaEnabled'
    : 'disable_mfaEnabled';
  const mfaEnabledValueText = getOptionValueText(
    MFA_ENABLED_FEATURE,
    hasMfaEnabledValue,
  );

  const hasRefundPaymentlinkEnabled = hasFeature(
    merchant?.configuration,
    'refundPaymentlinkEnabled',
  );
  const hasRefundPaymentlinkEnabledValue = hasRefundPaymentlinkEnabled
    ? 'enable_refundPaymentlinkEnabled'
    : 'disable_refundPaymentlinkEnabled';
  const refundPaymentlinkEnabledValueText = getOptionValueText(
    REFUND_PAYMENT_LINK_ENABLED_FEATURE,
    hasRefundPaymentlinkEnabledValue,
  );

  const hasMultimerchantEnabled = hasFeature(
    merchant?.configuration,
    'multimerchantEnabled',
  );
  const hasMultimerchantEnabledValue = hasMultimerchantEnabled
    ? 'enable_multimerchantEnabled'
    : 'disable_multimerchantEnabled';
  const multimerchantEnabledValueText = getOptionValueText(
    MULTIMERCHANT_ENABLED_FEATURE,
    hasMultimerchantEnabledValue,
  );

  // const hasPayboxEnabled = hasFeature(merchant?.configuration, 'payboxEnabled');
  // const hasPayboxEnabledValue = hasPayboxEnabled
  //   ? 'enable_payboxEnabled'
  //   : 'disable_payboxEnabled';
  // const payboxEnabledValueText = getOptionValueText(
  //   PAYBOX_ENABLED_FEATURE,
  //   hasPayboxEnabledValue,
  // );

  // @ts-ignore
  const appFeaturesFormFields = useMemo<FormFieldType[]>(() => {
    const inputs = [
      {
        type: 'dropdown',
        label: 'Discount Amount (Discount with fixed amount option)',
        alternativeLabel: 'Discount Amount',
        name: 'configuration',
        value: discountAmountValueText,
        dropdown: {
          placeholder: 'Select a type',
          values: DISCOUNT_AMOUNT_FEATURE,
          options: DISCOUNT_AMOUNT_FEATURE,
        },
      },
      {
        type: 'dropdown',
        label: 'Notes (Adding notes to transactions in the app)',
        alternativeLabel: 'Notes',
        name: 'configuration',
        value: notesValueText,
        dropdown: {
          placeholder: 'Select a type',
          values: NOTES_FEATURE,
          options: NOTES_FEATURE,
        },
      },
      {
        type: 'dropdown',
        label:
          'QR Payment Initiation (Next payment can be initiated via QR code instead card)',
        alternativeLabel: 'QR Payment Initiation Enabled',
        name: 'configuration',
        value: qrPaymentInitiationEnabledValueText,
        dropdown: {
          placeholder: 'Select a type',
          values: QR_PAYMENT_INITIATION_ENABLED_FEATURE,
          options: QR_PAYMENT_INITIATION_ENABLED_FEATURE,
        },
      },
      {
        type: 'dropdown',
        label: 'QR Payments (Enabling QR code payments in the app)',
        alternativeLabel: 'QR Payments',
        name: 'configuration',
        value: qrPaymentsValueText,
        dropdown: {
          placeholder: 'Select a type',
          values: QR_PAYMENT_FEATURE,
          options: QR_PAYMENT_FEATURE,
        },
      },
      {
        type: 'dropdown',
        label: 'Unlinked Refund (Enabling unlinked refunds in the app)',
        alternativeLabel: 'Unlinked Refund',
        name: 'configuration',
        value: unlinkedRefundValueText,
        dropdown: {
          placeholder: 'Select a type',
          values: UNLINKED_REFUND_FEATURE,
          options: UNLINKED_REFUND_FEATURE,
        },
      },
      {
        type: 'dropdown',
        label: 'Receipt Printing (Enabling receipt printing in the app)',
        alternativeLabel: 'Receipt Printing',
        name: 'configuration',
        value: receiptPrintingValueText,
        dropdown: {
          placeholder: 'Select a type',
          values: RECEIPT_PRINTING_FEATURE,
          options: RECEIPT_PRINTING_FEATURE,
        },
      },
      {
        type: 'dropdown',
        label: 'Multimerchant (Enabling multimerchant support in the app)',
        alternativeLabel: 'Multimerchant Enabled',
        name: 'configuration',
        value: multimerchantEnabledValueText,
        dropdown: {
          placeholder: 'Select a type',
          values: MULTIMERCHANT_ENABLED_FEATURE,
          options: MULTIMERCHANT_ENABLED_FEATURE,
        },
      },
    ];
    inputs.sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        title: 'App Feature Configurations',
        icon: <MobileOutlined />,
        inputs,
      },
    ];
  }, [
    discountAmountValueText,
    multimerchantEnabledValueText,
    notesValueText,
    qrPaymentInitiationEnabledValueText,
    qrPaymentsValueText,
    receiptPrintingValueText,
    unlinkedRefundValueText,
  ]);

  const formFieldsPortal = useMemo<FormFieldType[]>(() => {
    const inputsPortal = [
      {
        type: 'dropdown',
        label: 'AI Forecast (Enabling AI Forecast feature in the portal)',
        alternativeLabel: 'AI Forecast Enabled',
        name: 'configuration',
        value: aiForecastEnabledValueText,
        dropdown: {
          placeholder: 'Select a type',
          values: AI_FORECAST_ENABLED_FEATURE,
          options: AI_FORECAST_ENABLED_FEATURE,
        },
      },
      {
        type: 'dropdown',
        label:
          'Send Payment Link From Portal (Enabling sending payment links from the portal)',
        alternativeLabel: 'Send Payment Link From Portal',
        name: 'configuration',
        value: sendPaymentLinkFromPortalEnabledValueText,
        dropdown: {
          placeholder: 'Select a type',
          values: SEND_PAYMENT_LINK_FROM_PORTAL_ENABLED_FEATURE,
          options: SEND_PAYMENT_LINK_FROM_PORTAL_ENABLED_FEATURE,
        },
      },
      {
        type: 'dropdown',
        label:
          'MFA (Enabling multi-factor authentication in the portal and app)',
        alternativeLabel: 'MFA Enabled',
        name: 'configuration',
        value: mfaEnabledValueText,
        dropdown: {
          placeholder: 'Select a type',
          values: MFA_ENABLED_FEATURE,
          options: MFA_ENABLED_FEATURE,
        },
      },
      {
        type: 'dropdown',
        label:
          'Refund Payment Link (Enabling refund via payment links in the portal)',
        alternativeLabel: 'Refund Payment Link Enabled',
        name: 'configuration',
        value: refundPaymentlinkEnabledValueText,
        dropdown: {
          placeholder: 'Select a type',
          values: REFUND_PAYMENT_LINK_ENABLED_FEATURE,
          options: REFUND_PAYMENT_LINK_ENABLED_FEATURE,
        },
      },
      // {
      //   type: 'dropdown',
      //   label: 'Paybox (Enabling Paybox feature in the portal)',
      //   alternativeLabel: 'Paybox Enabled',
      //   name: 'configuration',
      //   value: payboxEnabledValueText,
      //   dropdown: {
      //     placeholder: 'Select a type',
      //     values: PAYBOX_ENABLED_FEATURE,
      //     options: PAYBOX_ENABLED_FEATURE,
      //   },
      // },
    ];
    inputsPortal.sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        title: 'Portal Feature Configurations',
        icon: <DesktopOutlined />,
        inputs: inputsPortal,
      },
    ];
  }, [
    aiForecastEnabledValueText,
    mfaEnabledValueText,
    refundPaymentlinkEnabledValueText,
    sendPaymentLinkFromPortalEnabledValueText,
  ]);

  const _onHandleSave = (fieldName: string, newValue: string) => {
    const dataType = typeof merchant?.configuration;
    const updateType = newValue.split('_')[0] as 'enable' | 'disable';
    const featureType = newValue.split('_')[1];
    // Is array
    if (dataType === 'object') {
      // is array, just push or pull
      let newArr = [];
      if (updateType === 'disable') {
        // @ts-ignore
        newArr = merchant?.configuration.filter(
          // @ts-ignore
          (feature) => feature !== featureType,
        );
        // @ts-ignore
        props.onSave(fieldName, newArr);
      }
      // Enable
      else {
        // @ts-ignore
        newArr = [...merchant?.configuration, featureType];
        console.log(newArr);
        // @ts-ignore
        props.onSave(fieldName, newArr);
      }
    }
    // is string
    else if (dataType === 'string') {
      if (updateType === 'disable') {
        // @ts-ignore
        props.onSave(fieldName, []);
      }
      // Enable
      else {
        // @ts-ignore
        newArr = [...merchant?.configuration, featureType];
        // @ts-ignore
        console.log(newArr);
        // @ts-ignore
        props.onSave(fieldName, newArr);
      }
    }
    // No current values; just add feature
    else {
      if (updateType === 'disable') {
        // We just add empty array
        // @ts-ignore
        props.onSave(fieldName, []);
      } else {
        // @ts-ignore
        props.onSave(fieldName, [featureType]);
      }
    }
  };

  return (
    <div>
      {appFeaturesFormFields.map((form) => (
        <Container key={form.title}>
          <FormGroup icon={form.icon} title={form.title}>
            {form.inputs.map((input) => (
              <FormGroupItem
                {...input}
                key={input.name}
                onSave={(newValue) =>
                  _onHandleSave(input.name, newValue as string)
                }
              />
            ))}
          </FormGroup>
        </Container>
      ))}
      <Space size="xl" />
      {formFieldsPortal.map((form) => (
        <Container key={form.title}>
          <FormGroup icon={form.icon} title={form.title}>
            {form.inputs.map((input) => (
              <FormGroupItem
                {...input}
                key={input.name}
                onSave={(newValue) =>
                  _onHandleSave(input.name, newValue as string)
                }
              />
            ))}
          </FormGroup>
        </Container>
      ))}
    </div>
  );
};

export { MerchantConfiguration };
